/*
 * Product hero behaviour
 * <lp-gallery>       scroll-snap slider + arrows + thumbnails + delayed social-proof banner
 * <lp-product-hero>  bundle (variant) selection, ATC via /cart/add.js, sticky ATC bar
 */
(function () {
  'use strict';

  /* ================================================================ gallery */
  class LPGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('.lp-gallery__thumb'));
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.index = 0;

      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.goTo(i)));
      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.index + 1));

      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => {
          const w = this.track.offsetWidth || 1;
          const idx = Math.round(this.track.scrollLeft / w);
          if (idx !== this.index) {
            this.index = idx;
            this.updateThumbs();
          }
          this.updateArrows();
        });
      }, { passive: true });

      this.onResize = () => {
        this.track.scrollTo({ left: this.index * this.track.offsetWidth, behavior: 'auto' });
        this.updateArrows();
      };
      window.addEventListener('resize', this.onResize);
      this.updateArrows();
      this.initBanner();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      if (this.io) this.io.disconnect();
      clearTimeout(this.bannerTimer);
    }

    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, i));
      this.track.scrollTo({ left: this.index * this.track.offsetWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.updateThumbs();
      this.updateArrows();
    }

    goToMedia(mediaId) {
      if (!mediaId) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }

    updateThumbs() {
      this.thumbs.forEach((t, i) => t.classList.toggle('is-active', i === this.index));
      const active = this.thumbs[this.index];
      const strip = active && active.parentElement;
      if (active && strip) {
        const left = active.offsetLeft - strip.clientWidth / 2 + active.clientWidth / 2;
        strip.scrollTo({ left, behavior: 'smooth' });
      }
    }

    updateArrows() {
      if (this.prev) this.prev.classList.toggle('is-disabled', this.index === 0);
      if (this.next) this.next.classList.toggle('is-disabled', this.index === this.slides.length - 1);
    }

    initBanner() {
      const banner = this.querySelector('[data-gallery-banner]');
      if (!banner) return;
      const delay = parseFloat(this.dataset.bannerDelay || '1.5') * 1000;
      const close = banner.querySelector('[data-gallery-banner-close]');
      if (close) {
        close.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          banner.classList.remove('is-visible');
          banner.classList.add('is-hidden');
        });
      }
      const show = () => {
        this.bannerTimer = setTimeout(() => {
          if (!banner.classList.contains('is-hidden')) banner.classList.add('is-visible');
        }, delay);
      };
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              show();
              this.io.disconnect();
            }
          });
        }, { threshold: 0.1 });
        this.io.observe(this.querySelector('.lp-gallery__stage') || this);
      } else {
        show();
      }
    }
  }
  if (!customElements.get('lp-gallery')) customElements.define('lp-gallery', LPGallery);

  /* ================================================================ product hero */
  class LPProductHero extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-lp-product-form]');
      this.variantInput = this.querySelector('[data-lp-variant-input]');
      this.cards = Array.from(this.querySelectorAll('.lp-bundle__card'));
      this.gallery = this.querySelector('lp-gallery');
      this.stickybar = this.querySelector('[data-lp-stickybar]');

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.select(card));
        card.addEventListener('keydown', (e) => this.onCardKey(e, card));
      });

      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));

      this.querySelectorAll('[data-lp-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const input = this.querySelector('input[data-lp-qty-input][type="number"]');
          if (!input) return;
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.lpQtyStep, 10));
          input.value = next;
        });
      });

      this.initStickyBar();
    }

    disconnectedCallback() {
      if (this.stickyIO) this.stickyIO.disconnect();
    }

    onCardKey(e, card) {
      const i = this.cards.indexOf(card);
      let target = null;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') target = this.cards[(i + 1) % this.cards.length];
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') target = this.cards[(i - 1 + this.cards.length) % this.cards.length];
      if (target) {
        e.preventDefault();
        target.focus();
        this.select(target);
      }
    }

    select(card) {
      this.cards.forEach((c) => {
        const on = c === card;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-checked', String(on));
      });
      if (this.variantInput) this.variantInput.value = card.dataset.variantId;
      this.updateButtons(card);

      if (this.dataset.jumpToVariantMedia === 'true' && this.gallery && card.dataset.mediaId) {
        this.gallery.goToMedia(card.dataset.mediaId);
      }

      /* keep ?variant= in the URL on product pages (native Shopify behaviour) */
      if (window.location.pathname.indexOf('/products/') > -1) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', card.dataset.variantId);
        history.replaceState(history.state, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('lp:variant:change', {
        bubbles: true,
        detail: { variantId: card.dataset.variantId, price: card.dataset.price, compare: card.dataset.compare },
      }));
    }

    updateButtons(card) {
      const available = card.dataset.available === 'true';
      this.querySelectorAll('[data-lp-atc]').forEach((btn) => {
        btn.disabled = !available;
        const label = btn.querySelector('[data-lp-atc-label]');
        if (label) label.textContent = available ? btn.dataset.label : btn.dataset.soldOutLabel;
        const price = btn.querySelector('[data-lp-atc-price]');
        const compare = btn.querySelector('[data-lp-atc-compare]');
        if (price) price.textContent = card.dataset.price || '';
        if (compare) compare.textContent = card.dataset.compare || '';
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      const submitter = event.submitter && event.submitter.matches('[data-lp-atc]') ? event.submitter : this.querySelector('[data-lp-atc]');
      if (!this.variantInput || !this.variantInput.value) return;
      const buttons = Array.from(this.querySelectorAll('[data-lp-atc]'));
      const errorEl = this.querySelector('[data-lp-atc-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (submitter) submitter.classList.add('is-loading');
      buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));

      const qtyInput = this.querySelector('[data-lp-qty-input]');
      const quantity = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const properties = {};
      new FormData(this.form).forEach((value, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      });
      const item = { id: Number(this.variantInput.value), quantity };
      if (Object.keys(properties).length) item.properties = properties;

      try {
        await window.LP.cart.add([item]);
      } catch (err) {
        console.error('[lp-hero] add to cart failed', err);
        if (errorEl) {
          errorEl.textContent = (err && err.message) || 'Could not add to cart. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        if (submitter) submitter.classList.remove('is-loading');
        buttons.forEach((b) => b.removeAttribute('aria-busy'));
      }
    }

    initStickyBar() {
      if (!this.stickybar) return;
      const main = this.querySelector('[data-lp-hero-main]');
      if (!main || !('IntersectionObserver' in window)) return;
      this.stickyIO = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.stickybar.classList.toggle('is-visible', visible);
        this.stickybar.setAttribute('aria-hidden', String(!visible));
        document.documentElement.classList.toggle('lp-has-stickybar', visible);
      }, { threshold: 0 });
      this.stickyIO.observe(main);
    }
  }
  if (!customElements.get('lp-product-hero')) customElements.define('lp-product-hero', LPProductHero);
})();